import * as THREE from 'three';
import Action from '../classes/Action';
import VisibilityManager from '../classes/VisibilityManager';
import { SaveModificationTypes, RenderModes } from '../globals';
import ProjectSaver from '../classes/ProjectSaver';
import { VisibilityModes } from "../interfaces/enums";
import DefaultSceneManager from '../classes/DefaultSceneManager';
import MaterialAsset from '../classes/MaterialAsset';
import ScenarioHealthUtils from '../classes/utilities/ScenarioHealthUtils';
import Scenario from '../classes/scenarioElements/Scenario';
import {
	ActionDescription,
	ActionParameters
} from "../classes/api/APIDecorators";
import {RegisterActionWithAPI} from "../classes/api/Decorators";

@ActionDescription("Sets the visibility mode of the objects in the given renderMode. visibilityModes can be an array of VisibilityModes or a single VisibilityMode. if VisibilityMode is Custom, custom material(s) may be provided as a material id")
@ActionParameters({objects: "Object3D[]", visibilityModes: "VisibilityModes | VisibilityModes[]", renderMode: "RenderModes", customMaterials: "string | undefined | (string | undefined)[]"})
@RegisterActionWithAPI("SetVisibilityModeAction")

export default class SetVisibilityModeAction extends Action
{
	constructor(objects: THREE.Object3D[], visibilityModes: VisibilityModes | VisibilityModes[], renderMode: RenderModes, customMaterials: string | undefined |  (string | undefined)[] = [])
	{
		if(!Array.isArray(visibilityModes)) visibilityModes = Array(objects.length).fill(visibilityModes);
		if(!Array.isArray(customMaterials)) customMaterials = Array(objects.length).fill(customMaterials);
		const oldValues: VisibilityModes[] = [];
		const oldCustomMaterials: string[] = [];
		for (const object of objects)
		{
			oldValues.push(object.userData.visibility[renderMode]);
			if(renderMode === RenderModes.Standalone)
			{
				oldCustomMaterials.push(object.userData.visibility.customStandaloneMaterial);
			}
			else
			{
				oldCustomMaterials.push(object.userData.visibility.customDeviceMaterial);
			}       
		}
		const actionName = objects.length > 1 ? "Set Objects Visibility Mode" : "Set " + objects[0].name + " Visibility To " + visibilityModes[0];

		super([objects, visibilityModes, renderMode, customMaterials], [objects, oldValues, renderMode, oldCustomMaterials], actionName);
	}

	do(objects: THREE.Object3D[], visibilityModes: VisibilityModes[], renderMode: RenderModes, customMaterials: string[])
	{
		for(let i = 0; i < objects.length; i++)
		{
			const object = objects[i];
			const visibilityMode = visibilityModes[i];
			const customMaterial = customMaterials[i] ?? undefined;
			if(visibilityMode === VisibilityModes.Custom)
			{
				DefaultSceneManager.getInstance().syncVisibilityMode(object, visibilityMode, renderMode, customMaterial);
				if(renderMode === RenderModes.Standalone)
				{
					object.userData.visibility.customStandaloneMaterial = customMaterial;
				}
				else
				{
					object.userData.visibility.customDeviceMaterial = customMaterial;
				}   
			}
			VisibilityManager.setObjectsVisibilityMode([object], [visibilityMode], renderMode, customMaterial);
			MaterialAsset.registerCustomMaterialReference(object);
			ScenarioHealthUtils.CheckCustomMaterialsForObject(object, Scenario.currentStep())
			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Visibility);
		}		
	}

	undo(objects: THREE.Object3D[], visibilityModes: VisibilityModes[], renderMode: RenderModes, customMaterials: string[])
	{
		for(let i = 0; i < objects.length; i++)
		{
			const object = objects[i];
			const visibilityMode = visibilityModes[i];
			const customMaterial = customMaterials[i];

			if(renderMode === RenderModes.Standalone)
			{
				object.userData.visibility.customStandaloneMaterial = customMaterial;
			}
			else
			{
				object.userData.visibility.customDeviceMaterial = customMaterial;
			}       

			VisibilityManager.setObjectsVisibilityMode([object], [visibilityMode], renderMode, customMaterial);
			ScenarioHealthUtils.CheckCustomMaterialsForObject(object, Scenario.currentStep())
		}
	}
}
